import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Shell from '../dashboard/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { DropdownMenu, DropdownItem, DropdownSeparator } from '../../components/ui/DropdownMenu';
import FeatureGate from '../../components/FeatureGate';

const EmailTemplates = () => {
  const { user } = useSelector(state => state.auth);
  const { store } = useSelector(state => state.store);
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailSettings, setEmailSettings] = useState({
    orderConfirmation: true,
    orderUpdates: true,
    welcomeEmails: true,
    refundNotifications: true,
    orderCancellation: true,
    orderDelivered: true,
    marketingEmails: false,
    smsNotifications: false,
    pushNotifications: false,
    emailFrequency: 'immediate',
    smsFrequency: 'immediate',
    pushFrequency: 'immediate'
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isTestOpen, setIsTestOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [templateContent, setTemplateContent] = useState({
    subject: '',
    htmlContent: '',
    textContent: '',
    enabled: true
  });

  // Fetch templates from backend
  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      // Don't use localStorage for tokens - use secure HTTP-only cookies
      const token = null;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/email-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }
      
      const data = await response.json();
      setTemplates(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update template
  const updateTemplate = async (templateId, updates) => {
    try {
      // Don't use localStorage for tokens - use secure HTTP-only cookies
      const token = null;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/email-templates/${templateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update template');
      }
      
      // Refresh templates
      await fetchTemplates();
    } catch (err) {
      setError(err.message);
      console.error('Error updating template:', err);
    }
  };

  // Send test email
  const sendTestEmail = async (templateId, testEmail) => {
    try {
      // Don't use localStorage for tokens - use secure HTTP-only cookies
      const token = null;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/email-templates/${templateId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ testEmail })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send test email');
      }
      
      return await response.json();
    } catch (err) {
      setError(err.message);
      console.error('Error sending test email:', err);
      throw err;
    }
  };

  // Get template preview
  const getTemplatePreview = async (templateId) => {
    try {
      // Don't use localStorage for tokens - use secure HTTP-only cookies
      const token = null;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/email-templates/${templateId}/preview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get template preview');
      }
      
      return await response.json();
    } catch (err) {
      setError(err.message);
      console.error('Error getting template preview:', err);
      throw err;
    }
  };

  // Fetch email settings from backend
  const fetchEmailSettings = async () => {
    try {
      // Don't use localStorage for tokens - use secure HTTP-only cookies
      const token = null;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/email-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch email settings');
      }
      
      const data = await response.json();
      if (data.success) {
        setEmailSettings(data.settings);
      }
    } catch (err) {
      console.error('Error fetching email settings:', err);
    }
  };

  // Update email settings
  const updateEmailSettings = async (newSettings) => {
    try {
      setSettingsLoading(true);
      // Don't use localStorage for tokens - use secure HTTP-only cookies
      const token = null;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/email-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update email settings');
      }
      
      const data = await response.json();
      if (data.success) {
        setEmailSettings(data.settings);
        alert('Email settings updated successfully!');
      }
    } catch (err) {
      console.error('Error updating email settings:', err);
      alert('Failed to update email settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Test email configuration
  const testEmailConfiguration = async (testEmail) => {
    try {
      // Don't use localStorage for tokens - use secure HTTP-only cookies
      const token = null;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/email-settings/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ testEmail })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send test email');
      }
      
      const data = await response.json();
      if (data.success) {
        alert('Test email sent successfully!');
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      alert('Failed to send test email');
    }
  };

  // Handle setting toggle
  const handleSettingToggle = (settingKey) => {
    const newSettings = {
      ...emailSettings,
      [settingKey]: !emailSettings[settingKey]
    };
    setEmailSettings(newSettings);
    updateEmailSettings(newSettings);
  };

  // Load templates and settings on component mount
  useEffect(() => {
    fetchTemplates();
    fetchEmailSettings();
  }, []);

  const sampleData = {
    orderNumber: 'ORD-123456',
    customerName: 'John Doe',
    orderTotal: '₦25,000',
    storeName: store?.name || 'Your Store',
    trackingNumber: 'TRK-789012',
    expectedDelivery: '3-5 business days',
    deliveryDate: new Date().toLocaleDateString(),
    cancellationReason: 'Customer request',
    refundAmount: '₦12,500',
    storeWebsite: 'https://yourstore.com',
    resetLink: 'https://yourstore.com/reset-password?token=abc123'
  };

  const categories = [
    { id: 'all', name: 'All Templates', icon: '📧' },
    { id: 'orders', name: 'Order Emails', icon: '📦' },
    { id: 'account', name: 'Account Emails', icon: '👤' },
    { id: 'marketing', name: 'Marketing', icon: '📢' }
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(template => template.category === selectedCategory);

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateContent({
      subject: template.subject,
      htmlContent: getTemplateHTML(template.id),
      textContent: getTemplateText(template.id),
      enabled: template.enabled
    });
    setIsEditOpen(true);
  };

  const handlePreviewTemplate = async (template) => {
    try {
      setLoading(true);
      const previewData = await getTemplatePreview(template.id);
      setSelectedTemplate({ ...template, previewHtml: previewData.previewHtml });
      setIsPreviewOpen(true);
    } catch (err) {
      setError('Failed to load template preview');
    } finally {
      setLoading(false);
    }
  };

  const handleTestTemplate = (template) => {
    setSelectedTemplate(template);
    setTestEmail('');
    setIsTestOpen(true);
  };

  const getTemplateHTML = (templateId) => {
    const templates = {
      'order-confirmation': {
        title: 'Order Confirmation',
        message: 'Thank you for your order! We\'re excited to prepare your items for shipment.',
        detailsTitle: 'Order Details',
        actionText: 'Track Your Order',
        actionUrl: '{{trackingUrl}}'
      },
      'order-shipped': {
        title: 'Your Order Has Shipped!',
        message: 'Great news! Your order is on its way to you.',
        detailsTitle: 'Shipping Information',
        actionText: 'Track Package',
        actionUrl: '{{trackingUrl}}'
      },
      'order-delivered': {
        title: 'Order Delivered Successfully',
        message: 'Your order has been delivered! We hope you love your purchase.',
        detailsTitle: 'Delivery Confirmation',
        actionText: 'Leave a Review',
        actionUrl: '{{reviewUrl}}'
      },
      'order-cancelled': {
        title: 'Order Cancelled',
        message: 'Your order has been cancelled as requested.',
        detailsTitle: 'Cancellation Details',
        actionText: 'Shop Again',
        actionUrl: '{{storeUrl}}'
      },
      'refund-processed': {
        title: 'Refund Processed',
        message: 'Your refund has been processed and will appear in your account within 3-5 business days.',
        detailsTitle: 'Refund Information',
        actionText: 'View Refund',
        actionUrl: '{{refundUrl}}'
      },
      'welcome-email': {
        title: 'Welcome to {{storeName}}!',
        message: 'Thank you for joining us! We\'re thrilled to have you as part of our community.',
        detailsTitle: 'Get Started',
        actionText: 'Start Shopping',
        actionUrl: '{{storeUrl}}'
      }
    };

    const template = templates[templateId] || templates['order-confirmation'];
    
    return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>{{subject}}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 0; background-color: #f8f9fa; }
        .container { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
        .header { background: linear-gradient(135deg, ${store?.theme?.primaryColor || '#000000'}, ${store?.theme?.secondaryColor || '#333333'}); color: white; padding: 30px 20px; text-align: center; }
        .header h1 { margin: 0; font-size: 28px; font-weight: 600; color: white; text-shadow: 0 2px 4px rgba(0,0,0,0.3); }
        .content { padding: 40px 30px; line-height: 1.6; color: #333333; }
        .content h2 { color: ${store?.theme?.primaryColor || '#000000'}; margin-top: 0; }
        .button { display: inline-block; background: ${store?.theme?.primaryColor || '#000000'}; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500; margin: 20px 0; }
        .footer { background-color: #f8f9fa; padding: 30px; text-align: center; color: #666666; font-size: 14px; }
        .order-details { background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>{{storeName}}</h1>
        </div>
        <div class="content">
          <h2>${template.title}</h2>
          <p>Hi {{customerName}},</p>
          <p>${template.message}</p>
          <div class="order-details">
            <h3>${template.detailsTitle}</h3>
            <p><strong>Order #:</strong> {{orderNumber}}</p>
            <p><strong>Amount:</strong> {{orderTotal}}</p>
            ${templateId === 'order-shipped' ? '<p><strong>Tracking #:</strong> {{trackingNumber}}</p>' : ''}
            ${templateId === 'refund-processed' ? '<p><strong>Refund Amount:</strong> {{refundAmount}}</p>' : ''}
          </div>
          <a href="${template.actionUrl}" class="button">${template.actionText}</a>
        </div>
        <div class="footer">
          <p>Thank you for choosing {{storeName}}!</p>
          <p>If you have any questions, please contact us at {{storeEmail}}</p>
        </div>
      </div>
    </body>
    </html>
    `;
  };

  const getTemplateText = (templateId) => {
    const templates = {
      'order-confirmation': {
        message: 'Thank you for your order! We\'re excited to prepare your items for shipment.',
        details: 'Order Details:',
        action: 'You can track your order using the link below.'
      },
      'order-shipped': {
        message: 'Great news! Your order is on its way to you.',
        details: 'Shipping Information:',
        action: 'Track your package using the tracking number provided.'
      },
      'order-delivered': {
        message: 'Your order has been delivered! We hope you love your purchase.',
        details: 'Delivery Confirmation:',
        action: 'Please consider leaving a review for your purchase.'
      },
      'order-cancelled': {
        message: 'Your order has been cancelled as requested.',
        details: 'Cancellation Details:',
        action: 'We hope to serve you again soon.'
      },
      'refund-processed': {
        message: 'Your refund has been processed and will appear in your account within 3-5 business days.',
        details: 'Refund Information:',
        action: 'You can view your refund details in your account.'
      },
      'welcome-email': {
        message: 'Thank you for joining us! We\'re thrilled to have you as part of our community.',
        details: 'Get Started:',
        action: 'Start exploring our products and enjoy your shopping experience.'
      }
    };

    const template = templates[templateId] || templates['order-confirmation'];
    
    return `Hi {{customerName}},

${template.message}

${template.details}
- Order #: {{orderNumber}}
- Amount: {{orderTotal}}
${templateId === 'order-shipped' ? '- Tracking #: {{trackingNumber}}' : ''}
${templateId === 'refund-processed' ? '- Refund Amount: {{refundAmount}}' : ''}

${template.action}

Thank you for choosing {{storeName}}!

Best regards,
{{storeName}} Team`;
  };

  const replaceVariables = (content, data) => {
    let result = content;
    Object.keys(data).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(regex, data[key]);
    });
    return result;
  };

  const handleSaveTemplate = () => {
    // Save template changes to backend
    console.log('Saving template:', editingTemplate.id, templateContent);
    setIsEditOpen(false);
    setEditingTemplate(null);
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    
    try {
      setLoading(true);
      await sendTestEmail(selectedTemplate.id, testEmail);
      alert('Test email sent successfully!');
      setIsTestOpen(false);
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplateStatus = async (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    
    try {
      await updateTemplate(templateId, { enabled: !template.enabled });
    } catch (err) {
      setError('Failed to update template status');
    }
  };

  return (
    <>
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <Shell>
      <FeatureGate featureId="marketing.email" upgradeMessage="Upgrade to access email templates and automated notifications">
      {/* Header Section */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
          <div>
            <h1 style={{ 
              fontSize: 'var(--font-size-3xl)', 
              fontWeight: 'var(--font-weight-bold)', 
              color: 'var(--color-gray-900)',
              marginBottom: 'var(--space-2)'
            }}>
              Email Templates
            </h1>
            <p style={{ 
              fontSize: 'var(--font-size-lg)', 
              color: 'var(--color-gray-600)' 
            }}>
              Manage and customize your automated email notifications
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                fontSize: 'var(--font-size-sm)', 
                color: 'var(--color-gray-500)',
                marginBottom: 'var(--space-1)'
              }}>
                Total Templates
              </div>
              <div style={{ 
                fontSize: 'var(--font-size-2xl)', 
                fontWeight: 'var(--font-weight-bold)', 
                color: 'var(--color-gray-900)' 
              }}>
                {templates.length}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                fontSize: 'var(--font-size-sm)', 
                color: 'var(--color-gray-500)',
                marginBottom: 'var(--space-1)'
              }}>
                Active
              </div>
              <div style={{ 
                fontSize: 'var(--font-size-2xl)', 
                fontWeight: 'var(--font-weight-bold)', 
                color: 'var(--color-green-600)' 
              }}>
                {templates.filter(t => t.enabled).length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
        <Card padding="sm" style={{ marginBottom: 'var(--space-6)' }}>
          <div style={{ 
            display: 'flex', 
            gap: 'var(--space-1)', 
            background: 'var(--color-gray-100)', 
            padding: 'var(--space-1)', 
            borderRadius: 'var(--radius-lg)',
            width: 'fit-content'
          }}>
            <button
              onClick={() => setActiveTab('templates')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-6)',
                borderRadius: 'var(--radius-md)',
                transition: 'all 0.2s ease',
                fontWeight: 'var(--font-weight-medium)',
                fontSize: 'var(--font-size-sm)',
                border: 'none',
                cursor: 'pointer',
                background: activeTab === 'templates' ? 'var(--color-primary)' : 'transparent',
                color: activeTab === 'templates' ? 'white' : 'var(--color-gray-600)',
                boxShadow: activeTab === 'templates' ? 'var(--shadow-sm)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'templates') {
                  e.target.style.background = 'var(--color-gray-50)';
                  e.target.style.color = 'var(--color-gray-900)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'templates') {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'var(--color-gray-600)';
                }
              }}
            >
              <span style={{ fontSize: 'var(--font-size-lg)' }}>📧</span>
              <span>Templates</span>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-6)',
                borderRadius: 'var(--radius-md)',
                transition: 'all 0.2s ease',
                fontWeight: 'var(--font-weight-medium)',
                fontSize: 'var(--font-size-sm)',
                border: 'none',
                cursor: 'pointer',
                background: activeTab === 'settings' ? 'var(--color-primary)' : 'transparent',
                color: activeTab === 'settings' ? 'white' : 'var(--color-gray-600)',
                boxShadow: activeTab === 'settings' ? 'var(--shadow-sm)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'settings') {
                  e.target.style.background = 'var(--color-gray-50)';
                  e.target.style.color = 'var(--color-gray-900)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'settings') {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'var(--color-gray-600)';
                }
              }}
            >
              <span style={{ fontSize: 'var(--font-size-lg)' }}>⚙️</span>
              <span>Settings</span>
            </button>
          </div>
        </Card>

      {activeTab === 'templates' && (
        <>
          {/* Loading State */}
          {loading && (
            <Card padding="lg" style={{ marginBottom: 'var(--space-6)', textAlign: 'center' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: 'var(--space-3)',
                padding: 'var(--space-8)'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid var(--color-gray-300)',
                  borderTop: '2px solid var(--color-primary)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{ color: 'var(--color-gray-600)' }}>Loading templates...</span>
              </div>
            </Card>
          )}

          {/* Error State */}
          {error && (
            <Card padding="lg" style={{ marginBottom: 'var(--space-6)' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                background: 'var(--color-red-50)',
                border: '1px solid var(--color-red-200)',
                borderRadius: 'var(--radius-lg)',
                color: 'var(--color-red-700)'
              }}>
                <span style={{ fontSize: 'var(--font-size-lg)' }}>⚠️</span>
                <span>{error}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={fetchTemplates}
                  style={{ marginLeft: 'auto' }}
                >
                  Retry
                </Button>
              </div>
            </Card>
          )}

          {/* Category Filter */}
          <Card padding="lg" style={{ marginBottom: 'var(--space-6)' }}>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 'var(--space-3)' 
            }}>
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-3) var(--space-6)',
                    borderRadius: 'var(--radius-xl)',
                    border: '2px solid',
                    transition: 'all 0.2s ease',
                    fontWeight: 'var(--font-weight-medium)',
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                    background: selectedCategory === category.id ? 'var(--color-primary)' : 'white',
                    color: selectedCategory === category.id ? 'white' : 'var(--color-gray-700)',
                    borderColor: selectedCategory === category.id ? 'var(--color-primary)' : 'var(--color-gray-200)',
                    boxShadow: selectedCategory === category.id ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
                    transform: selectedCategory === category.id ? 'scale(1.05)' : 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategory !== category.id) {
                      e.target.style.borderColor = 'var(--color-gray-300)';
                      e.target.style.boxShadow = 'var(--shadow-md)';
                      e.target.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategory !== category.id) {
                      e.target.style.borderColor = 'var(--color-gray-200)';
                      e.target.style.boxShadow = 'var(--shadow-sm)';
                      e.target.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <span style={{ fontSize: 'var(--font-size-lg)' }}>{category.icon}</span>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* Templates Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
            gap: 'var(--space-6)' 
          }}>
            {filteredTemplates.map(template => (
              <Card key={template.id} padding="lg" hover style={{ position: 'relative' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  justifyContent: 'space-between', 
                  marginBottom: 'var(--space-6)' 
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'linear-gradient(135deg, var(--color-gray-100), var(--color-gray-200))',
                      borderRadius: 'var(--radius-xl)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'var(--font-size-2xl)',
                      transition: 'transform 0.3s ease'
                    }}>
                      {template.icon}
                    </div>
                    <div>
                      <h3 style={{ 
                        fontWeight: 'var(--font-weight-bold)', 
                        color: 'var(--color-gray-900)', 
                        fontSize: 'var(--font-size-lg)',
                        marginBottom: 'var(--space-1)'
                      }}>
                        {template.name}
                      </h3>
                      <p style={{ 
                        fontSize: 'var(--font-size-sm)', 
                        color: 'var(--color-gray-600)', 
                        lineHeight: '1.5' 
                      }}>
                        {template.description}
                      </p>
                    </div>
                  </div>
                  <Badge 
                    variant={template.enabled ? 'success' : 'secondary'}
                    style={{ marginLeft: 'var(--space-2)' }}
                  >
                    {template.enabled ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                <div style={{ marginBottom: 'var(--space-6)' }}>
                  <div style={{ 
                    background: 'var(--color-gray-50)', 
                    borderRadius: 'var(--radius-lg)', 
                    padding: 'var(--space-3)',
                    marginBottom: 'var(--space-3)'
                  }}>
                    <p style={{ 
                      fontSize: 'var(--font-size-sm)', 
                      color: 'var(--color-gray-600)', 
                      marginBottom: 'var(--space-1)' 
                    }}>
                      Subject Line
                    </p>
                    <p style={{ 
                      fontSize: 'var(--font-size-sm)', 
                      fontWeight: 'var(--font-weight-medium)', 
                      color: 'var(--color-gray-900)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {template.subject}
                    </p>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-gray-500)'
                  }}>
                    <span>Last modified: {new Date(template.lastModified).toLocaleDateString()}</span>
                    <Badge variant="info" size="sm">{template.category}</Badge>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center' 
                }}>
                  <div style={{ 
                    display: 'flex', 
                    gap: 'var(--space-2)',
                    flexWrap: 'wrap'
                  }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreviewTemplate(template)}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                    >
                      <span>👁️</span>
                      <span>Preview</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestTemplate(template)}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                    >
                      <span>📤</span>
                      <span>Test</span>
                    </Button>
                  </div>
                  
                  <DropdownMenu
                    trigger={
                      <button
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--color-gray-300)',
                          background: 'var(--bg-primary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)',
                          color: 'var(--color-gray-600)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--color-gray-50)';
                          e.target.style.borderColor = 'var(--color-gray-400)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'var(--bg-primary)';
                          e.target.style.borderColor = 'var(--color-gray-300)';
                        }}
                      >
                        <span style={{ fontSize: 'var(--font-size-lg)' }}>⋯</span>
                      </button>
                    }
                    align="right"
                  >
                    <DropdownItem
                      icon="✏️"
                      onClick={() => handleEditTemplate(template)}
                    >
                      Edit Template
                    </DropdownItem>
                    <DropdownItem
                      icon="👁️"
                      onClick={() => handlePreviewTemplate(template)}
                    >
                      Preview Email
                    </DropdownItem>
                    <DropdownItem
                      icon="📤"
                      onClick={() => handleTestTemplate(template)}
                    >
                      Send Test Email
                    </DropdownItem>
                    <DropdownSeparator />
                    <DropdownItem
                      icon={template.enabled ? "❌" : "✅"}
                      variant={template.enabled ? "danger" : "success"}
                      onClick={() => toggleTemplateStatus(template.id)}
                    >
                      {template.enabled ? "Disable Template" : "Enable Template"}
                    </DropdownItem>
                  </DropdownMenu>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {activeTab === 'settings' && (
        <Card padding="xl">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'var(--space-3)', 
            marginBottom: 'var(--space-6)' 
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, var(--color-blue-500), var(--color-blue-600))',
              borderRadius: 'var(--radius-xl)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ color: 'white', fontSize: 'var(--font-size-lg)' }}>⚙️</span>
            </div>
            <div>
              <h3 style={{ 
                fontSize: 'var(--font-size-2xl)', 
                fontWeight: 'var(--font-weight-bold)', 
                color: 'var(--color-gray-900)',
                marginBottom: 'var(--space-1)'
              }}>
                Email Settings
              </h3>
              <p style={{ 
                fontSize: 'var(--font-size-base)', 
                color: 'var(--color-gray-600)' 
              }}>
                Configure your email notification preferences
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              padding: 'var(--space-4)', 
              background: 'var(--color-gray-50)', 
              borderRadius: 'var(--radius-xl)',
              transition: 'background-color 0.2s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'var(--color-green-100)',
                  borderRadius: 'var(--radius-lg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: 'var(--color-green-600)' }}>📧</span>
                </div>
                <div>
                  <h4 style={{ 
                    fontWeight: 'var(--font-weight-semibold)', 
                    color: 'var(--color-gray-900)',
                    marginBottom: 'var(--space-1)'
                  }}>
                    Order Confirmation Emails
                  </h4>
                  <p style={{ 
                    fontSize: 'var(--font-size-sm)', 
                    color: 'var(--color-gray-600)' 
                  }}>
                    Automatically send emails when orders are placed
                  </p>
                </div>
              </div>
              <label style={{ 
                position: 'relative', 
                display: 'inline-flex', 
                alignItems: 'center', 
                cursor: settingsLoading ? 'not-allowed' : 'pointer' 
              }}>
                <input 
                  type="checkbox" 
                  checked={emailSettings.orderConfirmation}
                  onChange={() => handleSettingToggle('orderConfirmation')}
                  disabled={settingsLoading}
                  style={{ position: 'absolute', opacity: 0, cursor: settingsLoading ? 'not-allowed' : 'pointer' }} 
                />
                <div style={{
                  width: '44px',
                  height: '24px',
                  background: emailSettings.orderConfirmation ? 'var(--color-primary)' : 'var(--color-gray-200)',
                  borderRadius: 'var(--radius-full)',
                  position: 'relative',
                  transition: 'background-color 0.2s ease',
                  opacity: settingsLoading ? 0.6 : 1
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    left: '2px',
                    width: '20px',
                    height: '20px',
                    background: 'white',
                    borderRadius: 'var(--radius-full)',
                    transition: 'transform 0.2s ease',
                    transform: emailSettings.orderConfirmation ? 'translateX(20px)' : 'translateX(0px)'
                  }} />
                </div>
              </label>
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              padding: 'var(--space-4)', 
              background: 'var(--color-gray-50)', 
              borderRadius: 'var(--radius-xl)',
              transition: 'background-color 0.2s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'var(--color-blue-100)',
                  borderRadius: 'var(--radius-lg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: 'var(--color-blue-600)' }}>📦</span>
                </div>
                <div>
                  <h4 style={{ 
                    fontWeight: 'var(--font-weight-semibold)', 
                    color: 'var(--color-gray-900)',
                    marginBottom: 'var(--space-1)'
                  }}>
                    Order Update Emails
                  </h4>
                  <p style={{ 
                    fontSize: 'var(--font-size-sm)', 
                    color: 'var(--color-gray-600)' 
                  }}>
                    Send emails when order status changes
                  </p>
                </div>
              </div>
              <label style={{ 
                position: 'relative', 
                display: 'inline-flex', 
                alignItems: 'center', 
                cursor: settingsLoading ? 'not-allowed' : 'pointer' 
              }}>
                <input 
                  type="checkbox" 
                  checked={emailSettings.orderUpdates}
                  onChange={() => handleSettingToggle('orderUpdates')}
                  disabled={settingsLoading}
                  style={{ position: 'absolute', opacity: 0, cursor: settingsLoading ? 'not-allowed' : 'pointer' }} 
                />
                <div style={{
                  width: '44px',
                  height: '24px',
                  background: emailSettings.orderUpdates ? 'var(--color-primary)' : 'var(--color-gray-200)',
                  borderRadius: 'var(--radius-full)',
                  position: 'relative',
                  transition: 'background-color 0.2s ease',
                  opacity: settingsLoading ? 0.6 : 1
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    left: '2px',
                    width: '20px',
                    height: '20px',
                    background: 'white',
                    borderRadius: 'var(--radius-full)',
                    transition: 'transform 0.2s ease',
                    transform: emailSettings.orderUpdates ? 'translateX(20px)' : 'translateX(0px)'
                  }} />
                </div>
              </label>
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              padding: 'var(--space-4)', 
              background: 'var(--color-gray-50)', 
              borderRadius: 'var(--radius-xl)',
              transition: 'background-color 0.2s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'var(--color-purple-100)',
                  borderRadius: 'var(--radius-lg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: 'var(--color-purple-600)' }}>👋</span>
                </div>
                <div>
                  <h4 style={{ 
                    fontWeight: 'var(--font-weight-semibold)', 
                    color: 'var(--color-gray-900)',
                    marginBottom: 'var(--space-1)'
                  }}>
                    Welcome Emails
                  </h4>
                  <p style={{ 
                    fontSize: 'var(--font-size-sm)', 
                    color: 'var(--color-gray-600)' 
                  }}>
                    Send welcome emails to new customers
                  </p>
                </div>
              </div>
              <label style={{ 
                position: 'relative', 
                display: 'inline-flex', 
                alignItems: 'center', 
                cursor: settingsLoading ? 'not-allowed' : 'pointer' 
              }}>
                <input 
                  type="checkbox" 
                  checked={emailSettings.welcomeEmails}
                  onChange={() => handleSettingToggle('welcomeEmails')}
                  disabled={settingsLoading}
                  style={{ position: 'absolute', opacity: 0, cursor: settingsLoading ? 'not-allowed' : 'pointer' }} 
                />
                <div style={{
                  width: '44px',
                  height: '24px',
                  background: emailSettings.welcomeEmails ? 'var(--color-primary)' : 'var(--color-gray-200)',
                  borderRadius: 'var(--radius-full)',
                  position: 'relative',
                  transition: 'background-color 0.2s ease',
                  opacity: settingsLoading ? 0.6 : 1
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    left: '2px',
                    width: '20px',
                    height: '20px',
                    background: 'white',
                    borderRadius: 'var(--radius-full)',
                    transition: 'transform 0.2s ease',
                    transform: emailSettings.welcomeEmails ? 'translateX(20px)' : 'translateX(0px)'
                  }} />
                </div>
              </label>
            </div>
            
            {/* Test Email Configuration */}
            <div style={{ 
              marginTop: 'var(--space-8)',
              padding: 'var(--space-6)',
              background: 'var(--color-blue-50)',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--color-blue-200)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--space-3)', 
                marginBottom: 'var(--space-4)' 
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'var(--color-blue-100)',
                  borderRadius: 'var(--radius-lg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: 'var(--color-blue-600)' }}>🧪</span>
                </div>
                <div>
                  <h4 style={{ 
                    fontWeight: 'var(--font-weight-semibold)', 
                    color: 'var(--color-gray-900)',
                    marginBottom: 'var(--space-1)'
                  }}>
                    Test Email Configuration
                  </h4>
                  <p style={{ 
                    fontSize: 'var(--font-size-sm)', 
                    color: 'var(--color-gray-600)' 
                  }}>
                    Send a test email to verify your configuration
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'end' }}>
                <div style={{ flex: 1 }}>
                  <Input
                    label="Test Email Address"
                    type="email"
                    placeholder="Enter email address to test"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    size="md"
                  />
                </div>
                <Button
                  onClick={() => testEmailConfiguration(testEmail)}
                  disabled={!testEmail || settingsLoading}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 'var(--space-2)',
                    minWidth: '140px',
                    justifyContent: 'center'
                  }}
                >
                  {settingsLoading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid transparent',
                        borderTop: '2px solid currentColor',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <span>Testing...</span>
                    </>
                  ) : (
                    <>
                      <span>📧</span>
                      <span>Send Test</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} size="lg">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <span className="text-white text-lg">👁️</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Email Preview</h2>
                <p className="text-gray-600">Preview how your email will look to customers</p>
              </div>
            </div>
            <Button 
              onClick={() => setIsPreviewOpen(false)}
              className="px-6 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
            >
              Close
            </Button>
          </div>
          <div className="border-2 border-gray-200 rounded-2xl p-6 bg-white max-h-96 overflow-y-auto shadow-inner">
            <div dangerouslySetInnerHTML={{ 
              __html: replaceVariables(getTemplateHTML(selectedTemplate?.id), sampleData) 
            }} />
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} size="lg">
        <div style={{ padding: 'var(--space-8)' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'var(--space-4)', 
            marginBottom: 'var(--space-8)' 
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, var(--color-green-500), var(--color-green-600))',
              borderRadius: 'var(--radius-xl)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ color: 'white', fontSize: 'var(--font-size-lg)' }}>✏️</span>
            </div>
            <div>
              <h2 style={{ 
                fontSize: 'var(--font-size-2xl)', 
                fontWeight: 'var(--font-weight-bold)', 
                color: 'var(--color-gray-900)',
                marginBottom: 'var(--space-1)'
              }}>
                Edit Template
              </h2>
              <p style={{ 
                fontSize: 'var(--font-size-base)', 
                color: 'var(--color-gray-600)' 
              }}>
                Customize your email template content and styling
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            <Input
              label="Subject Line"
              value={templateContent.subject}
              onChange={(e) => setTemplateContent(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Enter email subject"
              size="lg"
            />
            
            <Textarea
              label="HTML Content"
              value={templateContent.htmlContent}
              onChange={(e) => setTemplateContent(prev => ({ ...prev, htmlContent: e.target.value }))}
              rows={10}
              placeholder="Enter HTML content"
              size="lg"
              helperText="Use HTML tags to format your email content"
            />
            
            <Textarea
              label="Text Content"
              value={templateContent.textContent}
              onChange={(e) => setTemplateContent(prev => ({ ...prev, textContent: e.target.value }))}
              rows={6}
              placeholder="Enter plain text content"
              size="lg"
              helperText="Plain text version for email clients that don't support HTML"
            />
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 'var(--space-3)', 
            marginTop: 'var(--space-8)' 
          }}>
            <Button 
              variant="outline" 
              onClick={() => setIsEditOpen(false)}
              size="lg"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTemplate}
              size="lg"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Test Modal */}
      <Modal isOpen={isTestOpen} onClose={() => setIsTestOpen(false)}>
        <div style={{ padding: 'var(--space-8)' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'var(--space-4)', 
            marginBottom: 'var(--space-6)' 
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, var(--color-purple-500), var(--color-purple-600))',
              borderRadius: 'var(--radius-xl)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ color: 'white', fontSize: 'var(--font-size-lg)' }}>📤</span>
            </div>
            <div>
              <h2 style={{ 
                fontSize: 'var(--font-size-2xl)', 
                fontWeight: 'var(--font-weight-bold)', 
                color: 'var(--color-gray-900)',
                marginBottom: 'var(--space-1)'
              }}>
                Send Test Email
              </h2>
              <p style={{ 
                fontSize: 'var(--font-size-base)', 
                color: 'var(--color-gray-600)' 
              }}>
                Test your email template with sample data
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            <Input
              label="Test Email Address"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter test email address"
              size="lg"
            />
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm">ℹ️</span>
                </div>
                <div>
                  <p className="text-sm text-blue-800 font-medium">Test Email Information</p>
                  <p className="text-sm text-blue-700 mt-1">This will send a test email using the template with sample data. The email will be sent to the address you provide above.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-8">
            <Button 
              variant="outline" 
              onClick={() => setIsTestOpen(false)}
              className="px-6 py-3 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl font-medium transition-colors duration-200"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendTest} 
              disabled={!testEmail}
              className="px-6 py-3 bg-black text-white hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-xl font-medium transition-colors duration-200"
            >
              Send Test Email
            </Button>
          </div>
        </div>
      </Modal>
      </FeatureGate>
      </Shell>
    </>
  );
};

export default EmailTemplates;

